import JSZip from 'jszip';

import { formatSheet } from '@common/xlsxExport/xlsxExport';
import { gridToCsv, normalizePeriodCells } from '@common/csvExport/csvExport';
import { SheetExportTypes } from '@common/xlsxExport/xlsxExport.constants';

import saveAs from '../../shared/saveAs';

const sanitizeSheetName = name => (name || 'sheet').replace(/[/\\?%*:|"<>]/g, '_').slice(0, 200);

const buildSheetData = ({
  reportData,
  descriptionColumnIndex,
  hideDecimals,
  shouldDisplayZero,
  negativeMinus,
}) => [
  { name: 'GAAP', data: reportData.GAAP },
  { name: 'GAAP to STAT', data: reportData.GAAP_TO_STAT },
  { name: 'STAT', data: reportData.STAT },
  ...(reportData.ALL ? [{ name: 'ALL', data: reportData.ALL }] : []),
]
  .filter(sheet => sheet.data)
  .map(sheet => ({
    name: sheet.name,
    data: sheet.data,
    type: SheetExportTypes.REPORT,
    descriptionColumnIndex,
    options: {
      numberOfAdditionalRowsToStick: 2,
      hideDecimals,
      shouldDisplayZero,
      negativeMinus,
    },
  }));

const buildCsvSheets = (sheetData, description) => {
  const formatter = formatSheet({ description });
  const usedNames = new Set();

  return sheetData.map(sheet => {
    const { grid } = formatter(sheet);
    const baseName = sanitizeSheetName(sheet.name);
    let name = baseName;
    let counter = 1;
    while (usedNames.has(name)) {
      name = `${baseName}_${counter}`;
      counter += 1;
    }
    usedNames.add(name);

    return { name, content: gridToCsv(normalizePeriodCells(grid)) };
  });
};

const downloadSingleCsv = (csvSheet, fileName) => {
  const blob = new Blob([csvSheet.content], { type: 'text/csv;charset=utf-8' });
  saveAs({ blob, filenameWithExtension: `${fileName}.csv` });
};

const downloadCsvZip = (csvSheets, fileName) => {
  const zip = new JSZip();
  csvSheets.forEach(csvSheet => {
    zip.file(`${csvSheet.name}.csv`, csvSheet.content);
  });

  return zip.generateAsync({ type: 'blob' })
    .then(blob => {
      saveAs({ blob, filenameWithExtension: `${fileName}.zip` });
    });
};

export default ({
  reportData,
  description,
  fileName,
  hideDecimals = false,
  shouldDisplayZero,
  negativeMinus,
  descriptionColumnIndex,
}) => {
  const sheetData = buildSheetData({
    reportData,
    descriptionColumnIndex,
    hideDecimals,
    shouldDisplayZero,
    negativeMinus,
  });

  if (sheetData.length === 0) {
    return null;
  }

  const csvSheets = buildCsvSheets(sheetData, description);

  if (csvSheets.length === 1) {
    downloadSingleCsv(csvSheets[0], fileName);
    return null;
  }

  return downloadCsvZip(csvSheets, fileName);
};
